import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import WorkIcon from "@mui/icons-material/Work";

const CostCalculatorPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Trial Details
    trialDuration: 12,
    visitsPerMonth: 2,
    totalVisits: 24,

    // Travel Costs
    includeTravelCosts: true,
    distanceToSite: 50,
    travelMethod: "driving",
    roundTripCost: 100,
    parkingPerVisit: 20,
    hotelNightsPerVisit: 0,
    hotelCostPerNight: 150,
    mealsPerDay: 60,

    // Lost Wages
    includeWages: true,
    hourlyWage: 25,
    hoursLostPerVisit: 8,
    unpaidTimeOff: true,

    // Childcare/Dependent Care
    includeChildcare: false,
    childcareHoursPerVisit: 8,
    childcareHourlyRate: 15,

    // Medical Costs
    includeMedicalCosts: true,
    copayPerVisit: 25,
    prescriptionCopay: 30,
    prescriptionsPerMonth: 2,
    labTestsCopay: 50,
    labTestsPerVisit: 0.5, // Every other visit

    // Other Costs
    includeOtherCosts: false,
    otherCostsPerVisit: 0,
    otherDescription: "",

    // Insurance
    hasInsurance: true,
    insuranceCoveragePercent: 80,
    annualDeductible: 2000,
    deductibleMet: 0,

    // Tax Deductions
    trackTaxDeductions: true,
  });

  const [results, setResults] = useState(null);

  const steps = [
    "Trial Details",
    "Travel & Logistics",
    "Financial Impact",
    "Results",
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNext = () => {
    if (activeStep === steps.length - 2) {
      calculateCosts();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const calculateCosts = () => {
    const totalVisits = formData.totalVisits;

    // Travel Costs
    let travelCosts = 0;
    if (formData.includeTravelCosts) {
      travelCosts =
        formData.roundTripCost * totalVisits +
        formData.parkingPerVisit * totalVisits +
        formData.hotelNightsPerVisit *
          formData.hotelCostPerNight *
          totalVisits +
        formData.hotelNightsPerVisit * formData.mealsPerDay * totalVisits;
    }

    // Lost Wages
    let lostWages = 0;
    if (formData.includeWages && formData.unpaidTimeOff) {
      lostWages =
        formData.hourlyWage * formData.hoursLostPerVisit * totalVisits;
    }

    // Childcare
    let childcareCosts = 0;
    if (formData.includeChildcare) {
      childcareCosts =
        formData.childcareHoursPerVisit *
        formData.childcareHourlyRate *
        totalVisits;
    }

    // Medical Costs
    let medicalCosts = 0;
    if (formData.includeMedicalCosts) {
      const copays = formData.copayPerVisit * totalVisits;
      const prescriptions =
        formData.prescriptionCopay *
        formData.prescriptionsPerMonth *
        formData.trialDuration;
      const labTests =
        formData.labTestsCopay * (totalVisits * formData.labTestsPerVisit);
      medicalCosts = copays + prescriptions + labTests;
    }

    // Other Costs
    let otherCosts = 0;
    if (formData.includeOtherCosts) {
      otherCosts = formData.otherCostsPerVisit * totalVisits;
    }

    // Total Out-of-Pocket
    const totalOutOfPocket =
      travelCosts + lostWages + childcareCosts + medicalCosts + otherCosts;

    // Insurance Impact
    let insuranceCoverage = 0;
    let deductibleRemaining = Math.max(
      0,
      formData.annualDeductible - formData.deductibleMet
    );
    let afterDeductible = Math.max(0, medicalCosts - deductibleRemaining);

    if (formData.hasInsurance) {
      insuranceCoverage =
        afterDeductible * (formData.insuranceCoveragePercent / 100);
    }

    const netCost = totalOutOfPocket - insuranceCoverage;

    // Tax Deductible Amount (medical expenses > 7.5% of AGI can be deducted)
    const taxDeductibleAmount = medicalCosts + travelCosts * 0.5; // 50% of travel costs are deductible

    // Monthly Breakdown
    const monthlyAverage = netCost / formData.trialDuration;
    const costPerVisit = netCost / totalVisits;

    setResults({
      breakdown: {
        travel: travelCosts,
        wages: lostWages,
        childcare: childcareCosts,
        medical: medicalCosts,
        other: otherCosts,
      },
      totalOutOfPocket,
      insuranceCoverage,
      deductibleRemaining,
      netCost,
      taxDeductibleAmount,
      monthlyAverage,
      costPerVisit,
      totalVisits,
      trialDuration: formData.trialDuration,
    });
  };

  const handleSaveCalculation = () => {
    // TODO: Implement save to backend
    alert("Cost calculation saved! You can access it from your dashboard.");
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    alert("PDF export coming soon! This will generate a detailed cost report.");
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Clinical Trial Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Trial Duration (months)"
                type="number"
                value={formData.trialDuration}
                onChange={(e) =>
                  handleInputChange(
                    "trialDuration",
                    parseInt(e.target.value) || 0
                  )
                }
                helperText="How long will the trial last?"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Visits Per Month"
                type="number"
                value={formData.visitsPerMonth}
                onChange={(e) => {
                  const visits = parseInt(e.target.value) || 0;
                  handleInputChange("visitsPerMonth", visits);
                  handleInputChange(
                    "totalVisits",
                    visits * formData.trialDuration
                  );
                }}
                helperText="Average clinic visits per month"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Total Visits"
                type="number"
                value={formData.totalVisits}
                onChange={(e) =>
                  handleInputChange(
                    "totalVisits",
                    parseInt(e.target.value) || 0
                  )
                }
                helperText="Total visits during trial"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Based on your trial schedule, you'll have approximately{" "}
                <strong>{formData.totalVisits} visits</strong> over{" "}
                <strong>{formData.trialDuration} months</strong>.
              </Alert>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Travel & Logistics Costs
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeTravelCosts}
                    onChange={(e) =>
                      handleInputChange("includeTravelCosts", e.target.checked)
                    }
                  />
                }
                label="Include travel costs in calculation"
              />
            </Grid>

            {formData.includeTravelCosts && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Distance to Trial Site (miles)"
                    type="number"
                    value={formData.distanceToSite}
                    onChange={(e) => {
                      const distance = parseInt(e.target.value) || 0;
                      handleInputChange("distanceToSite", distance);
                      // Auto-calculate round trip cost based on IRS mileage rate
                      const roundTrip = Math.round(distance * 2 * 0.655);
                      handleInputChange("roundTripCost", roundTrip);
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Round Trip Cost"
                    type="number"
                    value={formData.roundTripCost}
                    onChange={(e) =>
                      handleInputChange(
                        "roundTripCost",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    helperText="Gas, tolls, or public transit"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Parking Per Visit"
                    type="number"
                    value={formData.parkingPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "parkingPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hotel Nights Per Visit"
                    type="number"
                    value={formData.hotelNightsPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "hotelNightsPerVisit",
                        parseInt(e.target.value) || 0
                      )
                    }
                    helperText="If overnight stay required"
                  />
                </Grid>

                {formData.hotelNightsPerVisit > 0 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Hotel Cost Per Night"
                        type="number"
                        value={formData.hotelCostPerNight}
                        onChange={(e) =>
                          handleInputChange(
                            "hotelCostPerNight",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Meals Per Day"
                        type="number"
                        value={formData.mealsPerDay}
                        onChange={(e) =>
                          handleInputChange(
                            "mealsPerDay",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Financial Impact
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            {/* Lost Wages */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeWages}
                    onChange={(e) =>
                      handleInputChange("includeWages", e.target.checked)
                    }
                  />
                }
                label="Include lost wages"
              />
            </Grid>

            {formData.includeWages && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hourly Wage"
                    type="number"
                    value={formData.hourlyWage}
                    onChange={(e) =>
                      handleInputChange(
                        "hourlyWage",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hours Lost Per Visit"
                    type="number"
                    value={formData.hoursLostPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "hoursLostPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    helperText="Including travel time"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.unpaidTimeOff}
                        onChange={(e) =>
                          handleInputChange("unpaidTimeOff", e.target.checked)
                        }
                      />
                    }
                    label="This is unpaid time off (will be counted as lost income)"
                  />
                </Grid>
              </>
            )}

            <Divider sx={{ my: 2, width: "100%" }} />

            {/* Childcare */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeChildcare}
                    onChange={(e) =>
                      handleInputChange("includeChildcare", e.target.checked)
                    }
                  />
                }
                label="Include childcare/dependent care costs"
              />
            </Grid>

            {formData.includeChildcare && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Childcare Hours Per Visit"
                    type="number"
                    value={formData.childcareHoursPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "childcareHoursPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hourly Rate"
                    type="number"
                    value={formData.childcareHourlyRate}
                    onChange={(e) =>
                      handleInputChange(
                        "childcareHourlyRate",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}

            <Divider sx={{ my: 2, width: "100%" }} />

            {/* Medical Costs */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeMedicalCosts}
                    onChange={(e) =>
                      handleInputChange("includeMedicalCosts", e.target.checked)
                    }
                  />
                }
                label="Include medical costs (copays, prescriptions)"
              />
            </Grid>

            {formData.includeMedicalCosts && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Copay Per Visit"
                    type="number"
                    value={formData.copayPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "copayPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Prescription Copay"
                    type="number"
                    value={formData.prescriptionCopay}
                    onChange={(e) =>
                      handleInputChange(
                        "prescriptionCopay",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Prescriptions Per Month"
                    type="number"
                    value={formData.prescriptionsPerMonth}
                    onChange={(e) =>
                      handleInputChange(
                        "prescriptionsPerMonth",
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Lab Tests Copay"
                    type="number"
                    value={formData.labTestsCopay}
                    onChange={(e) =>
                      handleInputChange(
                        "labTestsCopay",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Lab Tests Per Visit"
                    type="number"
                    value={formData.labTestsPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "labTestsPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    helperText="0.5 = every other visit"
                  />
                </Grid>

                <Divider sx={{ my: 2, width: "100%" }} />

                {/* Insurance */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Insurance Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.hasInsurance}
                        onChange={(e) =>
                          handleInputChange("hasInsurance", e.target.checked)
                        }
                      />
                    }
                    label="I have health insurance"
                  />
                </Grid>

                {formData.hasInsurance && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Insurance Coverage %"
                        type="number"
                        value={formData.insuranceCoveragePercent}
                        onChange={(e) =>
                          handleInputChange(
                            "insuranceCoveragePercent",
                            parseInt(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        helperText="After deductible"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Annual Deductible"
                        type="number"
                        value={formData.annualDeductible}
                        onChange={(e) =>
                          handleInputChange(
                            "annualDeductible",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Deductible Already Met"
                        type="number"
                        value={formData.deductibleMet}
                        onChange={(e) =>
                          handleInputChange(
                            "deductibleMet",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 2, width: "100%" }} />

            {/* Other Costs */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeOtherCosts}
                    onChange={(e) =>
                      handleInputChange("includeOtherCosts", e.target.checked)
                    }
                  />
                }
                label="Include other costs"
              />
            </Grid>

            {formData.includeOtherCosts && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other Costs Per Visit"
                    type="number"
                    value={formData.otherCostsPerVisit}
                    onChange={(e) =>
                      handleInputChange(
                        "otherCostsPerVisit",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.otherDescription}
                    onChange={(e) =>
                      handleInputChange("otherDescription", e.target.value)
                    }
                    placeholder="e.g., Pet care, home care"
                  />
                </Grid>
              </>
            )}

            <Divider sx={{ my: 2, width: "100%" }} />

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.trackTaxDeductions}
                    onChange={(e) =>
                      handleInputChange("trackTaxDeductions", e.target.checked)
                    }
                  />
                }
                label="Track potential tax deductions (medical expenses)"
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          results && (
            <Box>
              {/* Summary Card */}
              <Card
                elevation={3}
                sx={{
                  mb: 3,
                  background:
                    "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      ${results.netCost.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      Estimated Net Cost for {results.trialDuration}-Month Trial
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                        mt: 2,
                      }}
                    >
                      <Chip
                        label={`$${results.monthlyAverage.toLocaleString()}/month`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`$${results.costPerVisit.toLocaleString()}/visit`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Paper elevation={2} sx={{ mb: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.1)" }}>
                        <TableCell>
                          <strong>Cost Category</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Per Visit</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.breakdown.travel > 0 && (
                        <TableRow>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <DirectionsCarIcon
                                fontSize="small"
                                color="action"
                              />
                              Travel & Logistics
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${results.breakdown.travel.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            $
                            {(
                              results.breakdown.travel / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.breakdown.wages > 0 && (
                        <TableRow>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <WorkIcon fontSize="small" color="action" />
                              Lost Wages
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${results.breakdown.wages.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            $
                            {(
                              results.breakdown.wages / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.breakdown.childcare > 0 && (
                        <TableRow>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ChildCareIcon fontSize="small" color="action" />
                              Childcare
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${results.breakdown.childcare.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            $
                            {(
                              results.breakdown.childcare / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.breakdown.medical > 0 && (
                        <TableRow>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LocalHospitalIcon
                                fontSize="small"
                                color="action"
                              />
                              Medical Costs
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${results.breakdown.medical.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            $
                            {(
                              results.breakdown.medical / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.breakdown.other > 0 && (
                        <TableRow>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ReceiptIcon fontSize="small" color="action" />
                              Other Costs
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            ${results.breakdown.other.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            $
                            {(
                              results.breakdown.other / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}>
                        <TableCell>
                          <strong>Total Out-of-Pocket</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${results.totalOutOfPocket.toLocaleString()}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            $
                            {(
                              results.totalOutOfPocket / results.totalVisits
                            ).toFixed(2)}
                          </strong>
                        </TableCell>
                      </TableRow>
                      {results.insuranceCoverage > 0 && (
                        <TableRow>
                          <TableCell sx={{ color: "success.main" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AccountBalanceIcon fontSize="small" />
                              Insurance Coverage
                            </Box>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: "success.main" }}
                          >
                            -${results.insuranceCoverage.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: "success.main" }}
                          >
                            -$
                            {(
                              results.insuranceCoverage / results.totalVisits
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.1)" }}>
                        <TableCell>
                          <strong>NET COST</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>${results.netCost.toLocaleString()}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>${results.costPerVisit.toFixed(2)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Accordions for Additional Info */}
              {results.deductibleRemaining > 0 && (
                <Accordion elevation={2}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">
                      Insurance Deductible Status
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Annual Deductible"
                          secondary={`$${formData.annualDeductible.toLocaleString()}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Already Met"
                          secondary={`$${formData.deductibleMet.toLocaleString()}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Remaining to Meet"
                          secondary={`$${results.deductibleRemaining.toLocaleString()}`}
                        />
                      </ListItem>
                    </List>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Once you meet your deductible, insurance will cover{" "}
                      {formData.insuranceCoveragePercent}% of eligible medical
                      costs.
                    </Alert>
                  </AccordionDetails>
                </Accordion>
              )}

              {formData.trackTaxDeductions &&
                results.taxDeductibleAmount > 0 && (
                  <Accordion elevation={2} sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">
                        Tax Deduction Potential
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>
                            Potential Tax Deductible Amount: $
                            {results.taxDeductibleAmount.toLocaleString()}
                          </strong>
                        </Typography>
                        <Typography variant="body2">
                          Medical expenses exceeding 7.5% of your adjusted gross
                          income (AGI) may be tax deductible. This includes:
                        </Typography>
                      </Alert>
                      <List dense>
                        <ListItem>
                          <ListItemText secondary="• All medical costs (copays, prescriptions, lab tests)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText secondary="• 50% of travel costs for medical care" />
                        </ListItem>
                        <ListItem>
                          <ListItemText secondary="• Parking and tolls for medical visits" />
                        </ListItem>
                      </List>
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          <strong>Disclaimer:</strong> This is an estimate.
                          Consult with a tax professional for accurate deduction
                          calculations.
                        </Typography>
                      </Alert>
                    </AccordionDetails>
                  </Accordion>
                )}

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveCalculation}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  }}
                >
                  Save Calculation
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPDF}
                >
                  Export to PDF
                </Button>
              </Box>

              {/* Financial Assistance Alert */}
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Financial Assistance Available</strong>
                </Typography>
                <Typography variant="body2">
                  Many clinical trials offer travel reimbursement, parking
                  vouchers, and meal stipends. Some sponsors also provide
                  financial assistance for participants. Contact the trial
                  coordinator to learn about available support programs.
                </Typography>
              </Alert>
            </Box>
          )
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CalculateIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Cost Calculator
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Estimate total costs for clinical trial participation
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Calculate all potential costs including travel, lost wages, childcare,
        and medical expenses. Get a comprehensive view of your financial
        commitment.
      </Alert>

      {/* Stepper */}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<NavigateBeforeIcon />}
            disabled={activeStep === 0}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={
              activeStep === steps.length - 1 ? null : <NavigateNextIcon />
            }
            disabled={activeStep === steps.length - 1}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            {activeStep === steps.length - 2
              ? "Calculate Costs"
              : activeStep === steps.length - 1
              ? "Done"
              : "Next"}
          </Button>
        </Box>
      </Paper>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> This calculator provides estimates based
          on the information you provide. Actual costs may vary. This tool does
          not constitute financial or tax advice. Consult with financial and tax
          professionals for personalized guidance.
        </Typography>
      </Paper>
    </Container>
  );
};

export default CostCalculatorPage;
